import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { faChevronDown, faChevronRight, faGripDotsVertical, faList, faLock, faRotateExclamation } from '@fortawesome/pro-solid-svg-icons'
import { faFolder as faFolderOutline } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Checkbox, Space, Switch, Table, Tag, Tooltip } from 'antd'
import React, { HTMLAttributes, useState } from 'react'
import { blue, gold } from '@ant-design/colors'
import { centsToDollarsDisplay } from '../displayFormatters'
import { Action as LineItemAction, ActionType as LineItemActionType, LineItem as LineItemModel, renderPricingType } from './lineItemsModel'
import { Action, ActionType, ProposalCreationParameters } from './proposalsModel'
import { PricingTypes } from '../../common/constants'
import LineItem from './LineItem'
import BulkActions from './BulkActions'

type RowProps = HTMLAttributes<HTMLTableRowElement> & {
  'data-row-key': string
}

const DraggableTableRow = (props: RowProps) => {
  const id = props['data-row-key']
  const { listeners, setNodeRef, setActivatorNodeRef, attributes, transform, transition, active } = useSortable({ id })

  const dragStyle: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
  }

  return <tr {...props} ref={setNodeRef} style={dragStyle} {...attributes}>
    {React.Children.map(props.children, child => {
      if ((child as React.ReactElement).key === 'sort') {
        return React.cloneElement(child as React.ReactElement, {
          children: (
            <div
              ref={setActivatorNodeRef}
              style={{ cursor: id === active?.id ? 'grabbing' : 'grab' }}
              {...listeners}
            >
              <FontAwesomeIcon icon={faGripDotsVertical} />
            </div>
          )
        })
      } else {
        return child
      }
    })}
  </tr>
}
type Props = {
  model: ProposalCreationParameters,
  dispatch: (action: Action) => void
  dispatchLineItem: (id: number) => (action: LineItemAction) => void
  redesign?: boolean
}

const Workareas = ({ model, dispatch, dispatchLineItem, redesign = false }: Props) => {
  const [expandedRows, setExpandedRows] = useState<number[]>([])
  const handleLineItemEnabledChange = (id: number, checked: boolean) => {
    dispatchLineItem(id)({ type: LineItemActionType.EnabledChanged, value: checked })
    if (checked) {
      setExpandedRows([id])
    }
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active && over) {
      dispatch({ type: ActionType.LineItemMoved, id: active.id as number, over: over.id as number })
    }
  }

  return <DndContext onDragEnd={handleDragEnd}>
    <SortableContext items={model.lineItems.map(x => x.id)} strategy={verticalListSortingStrategy}>
      <Table
        dataSource={model.lineItems}
        rowKey="id"
        tableLayout={redesign ? 'fixed' : undefined}
        components={{
          body: {
            row: DraggableTableRow
          }
        }}
        title={() => <BulkActions jobId={model.jobId} lineItems={model.lineItems} dispatchLineItem={dispatchLineItem} />}
        columns={redesign ? [
          { key: 'sort', width: 40 },
          {
            key: 'expand',
            width: 32,
            render: (_, lineItem) => {
              const expanded = expandedRows.includes(lineItem.id)
              return lineItem.enabled && <button
                className="manage-proposal__expand"
                aria-label={expanded ? 'Collapse work area' : 'Expand work area'}
                onClick={() => expanded ? setExpandedRows([]) : setExpandedRows([lineItem.id])}
              >
                <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
              </button>
            }
          },
          {
            dataIndex: 'enabled',
            key: 'enabled',
            title: 'Show',
            width: 70,
            render: (enabled, lineItem) => <Checkbox
              checked={enabled}
              onChange={e => handleLineItemEnabledChange(lineItem.id, e.target.checked)}
            />,
          },
          {
            key: 'locked',
            align: 'center',
            width: 40,
            render: (_, lineItem) => {
              if (lineItem.locked && lineItem.enabled) {
                return <Tooltip title="This work area is locked"><FontAwesomeIcon color={blue.primary} icon={faLock} /></Tooltip>
              }
            },
          },
          {
            title: 'Workarea',
            dataIndex: 'title',
            key: 'title',
            width: 360,
            render: (title, lineItem) => <Space size={8}>
              <FontAwesomeIcon icon={faFolderOutline} color="#8c8c8c" />
              <strong>{title}</strong>
              {lineItem.isOnHold && <Tag color="orange">On Hold</Tag>}
            </Space>
          },
          {
            title: 'Pricing Type',
            key: 'pricingType',
            width: 140,
            render: (_, lineItem) => <span>{renderPricingType(lineItem.pricingType)}</span>
          },
          {
            key: 'itemization',
            title: 'Show Details',
            width: 220,
            render: (_, lineItem) => {
              if (!lineItem.enabled) {
                return <></>
              }
              return <Space align="start" direction="vertical" size={0}>
                <Space align="start">
                  {lineItem.showItems && <Tooltip title="This work area shows items"><FontAwesomeIcon color={blue.primary} icon={faList} /></Tooltip>}
                  {lineItem.showItems && <span>
                    Name
                    {lineItem.selectedItemColumns.includes('quantityAndUnits') && ', Quantity, Units'}
                    {lineItem.selectedItemColumns.includes('totalPrice') && ', Total Price'}
                  </span>}
                </Space>
                {lineItem.showItems && lineItem.selectedItemGuids.length > 0 && <span className="manage-proposal__row-meta">
                  {lineItem.selectedItemGuids.length} Items
                </span>}
              </Space>
            },
          },
          {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            align: 'right',
            width: 130,
            render: (price: number, lineItem: LineItemModel) => {
              const isQuotedOrPerVisit = lineItem.pricingType === PricingTypes.QuotedPrice || lineItem.pricingType === PricingTypes.PerVisit
              const showPriceMismatchWarning = isQuotedOrPerVisit && lineItem.price !== lineItem.estimatedPrice
              return <Space>
                {showPriceMismatchWarning && <span style={{ fontSize: '1.3em' }}><Tooltip title="Final Price does not match Estimated Price"><FontAwesomeIcon icon={faRotateExclamation} color={gold.primary} /></Tooltip></span>}
                {centsToDollarsDisplay(price)}{lineItem.pricingType === PricingTypes.PerVisit && 'per Visit'}
              </Space>
            }
          },
        ] : [
          { key: 'sort' },
          {
            key: 'expand',
            render: (_, lineItem) => {
              const expanded = expandedRows.includes(lineItem.id)
              const expandedClassName = expanded ? 'ant-table-row-expand-icon-expanded' : 'ant-table-row-expand-icon-collapsed'
              return lineItem.enabled && <button
                className={`ant-table-row-expand-icon ${expandedClassName}`}
                onClick={() => expanded ? setExpandedRows([]) : setExpandedRows([lineItem.id])}
              />
            }
          },
          {
            dataIndex: 'enabled',
            key: 'enabled',
            title: 'Show',
            render: (enabled, lineItem) => <Switch checked={enabled} onChange={value => handleLineItemEnabledChange(lineItem.id, value)} />,
          },
          {
            key: 'locked',
            align: 'center',
            render: (_, lineItem) => {
              if (lineItem.locked && lineItem.enabled) {
                return <Tooltip title="This work area is locked"><FontAwesomeIcon color={blue.primary} icon={faLock} /></Tooltip>
              }
            },
          },
          {
            title: 'Workarea',
            dataIndex: 'title',
            key: 'title',
            render: (title, lineItem) => <Space>{title} {lineItem.isOnHold && <Tag color="orange">On Hold</Tag>}</Space>
          },
          {
            key: 'itemization',
            title: 'Show Items',
            render: (_, lineItem) => {
              if (!lineItem.enabled) {
                return <></>
              }
              return <Space align="start">
                {lineItem.showItems && <Tooltip title="This work area shows items"><FontAwesomeIcon color={blue.primary} icon={faList} /></Tooltip>}
                {lineItem.showItems && <span>
                  Name
                  {lineItem.selectedItemColumns.includes('quantityAndUnits') && ', Quantity, Units'}
                  {lineItem.selectedItemColumns.includes('totalPrice') && ', Total Price'}
                </span>}
              </Space>
            },
          },
          {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            align: 'right',
            render: (price: number, lineItem: LineItemModel) => {
              const isQuotedOrPerVisit = lineItem.pricingType === PricingTypes.QuotedPrice || lineItem.pricingType === PricingTypes.PerVisit
              const showPriceMismatchWarning = isQuotedOrPerVisit && lineItem.price !== lineItem.estimatedPrice
              return <Space>
                {showPriceMismatchWarning && <span style={{ fontSize: '1.3em' }}><Tooltip title="Final Price does not match Estimated Price"><FontAwesomeIcon icon={faRotateExclamation} color={gold.primary} /></Tooltip></span>}
                {centsToDollarsDisplay(price)}{lineItem.pricingType === PricingTypes.PerVisit && 'per Visit'}
              </Space>
            }
          },
        ]}
        pagination={false}
        expandable={{
          expandedRowRender: (lineItem) => <div style={{ paddingLeft: 48 }}>
            <LineItem
              lineItem={lineItem}
              key={`line-item-${lineItem.id}`}
              dispatch={dispatchLineItem(lineItem.id)}
              jobId={model.jobId}
              customerId={model.customerId}
              redesign={redesign}
            />
          </div>,
          rowExpandable: (lineItem) => lineItem.enabled,
          expandedRowKeys: expandedRows,
          expandRowByClick: true,
          showExpandColumn: false,
          onExpand: (expanded, lineItem) => setExpandedRows(expanded ? [lineItem.id] : []),
        }}
        onRow={(lineItem) => ({
          style: { cursor: lineItem.enabled ? 'pointer' : 'initial' }
        })}
      />
    </SortableContext>
  </DndContext>
}

export default Workareas
